import React, { useEffect, useRef, useState } from 'react';

import { useForm, useFieldArray } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { BaseCard } from '../../components/NurturingCampaign/BaseCard';
import { RuleCard } from '../../components/NurturingCampaign/RuleCard';
import { SendTestModal } from '../../components/NurturingCampaign/SendTestModal';
import { ButtonInput } from '../../components/Shared/Inputs/ButtonInput';
import StyledSwitchInput from '../../components/Shared/StyledInputs/StyledSwitchInput';
import { StyledTextInput } from '../../components/Shared/StyledInputs/StyledTextInput';
import { StyledSelectInput } from '../../components/Shared/StyledInputs/StyledSelectInput';
import { useNurtureCampaign } from '../../data/nurtureCampaigns/useNurtureCampaign';
import LoadingWheel from '../../components/Shared/LoadingWheel';
import { useNurtureCampaignMutations } from '../../data/nurtureCampaigns/useNurtureCampaignMutations';
import { withAgency } from '../../data/withAgency';
import { INurtureCampaignRule, NurtureAction, NurtureCampaignRuleCreate, NurtureCampaignRuleUpdate } from '../../models/nurtureCampaign/NurtureCampaignRule';
import { INurtureCampaignCondition, NurtureCampaignConditionCreate, NurtureCampaignConditionUpdate, ConditionType } from '../../models/nurtureCampaign/NurtureCampaignCondition';
import { ConditionRow } from '../../components/NurturingCampaign/ConditionRow';
import useNotification from '../../components/notification/useNotifications';
import { Alert } from '../../components/Shared/Alert';
import { PlusIcon } from '@heroicons/react/solid';
import { PlusCircleIcon } from '@heroicons/react/outline';
import IconButtonDivider from '../../components/Shared/IconButtonDivider';
import Breadcrumbs from '../../components/Shared/Breadcrumbs';

export interface ConditionFormFields {
  id?: string;
  type: string;
  fieldName: string;
  operator: string;
  value: string;
  nurtureCampaignRuleId?: string;
  marketingGroupIds?: string[];
  multiValue?: string[];
  formIds?: string[];
}

export interface RuleFormFields {
  id?: string;
  enabled: boolean;
  name: string;
  conditions: ConditionFormFields[];
  delay: string;
  delaySpan: "hours" | "days";
  action: string;
  subject: string;
  content: string;
  enableButton: boolean;
  buttonText: string;
  buttonUrl: string;
  nurtureCampaignId: string;
  nextRuleId?: string;
  customUrl?: string;
}

export interface NurtureCampaignFormFields {
  id?: string;
  enabled: boolean;
  name: string;
  trigger: string;
  rules: RuleFormFields[];
  conditions: ConditionFormFields[];
}

interface NurturingCampaignBuilderProps {
  agency: any;
  agencyReady: boolean;
}

const ruleTemplate = {
  enabled: false,
  name: '',
  conditions: [],
  delay: '1',
  delaySpan: 'days' as "days" | "hours",
  action: '1',
  subject: '',
  content: '',
  enableButton: false,
  buttonText: "Click Here",
  buttonUrl: "{{agency.Domain}}/valuation",
  nurtureCampaignId: ''
}

function _NurturingCampaignBuilder({ agency, agencyReady }: NurturingCampaignBuilderProps) {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const { state } = useLocation();
  const { addNotification } = useNotification();
  const { campaignId } = useParams();
  const { nurtureCampaign } = useNurtureCampaign(agency.id, campaignId);
  const { createCampaign, createRule, updateCampaign, updateRule } = useNurtureCampaignMutations(agency.id);
  const { control, register, setValue, getValues, watch, handleSubmit, reset, formState: { errors } } = useForm<NurtureCampaignFormFields>({
    defaultValues: {
      enabled: false,
      name: '',
      trigger: '1',
      rules: []
    }
  });
  const { fields: rules, append: appendRule, remove: removeRule, move: moveRule } = useFieldArray({ control, name: "rules" });
  const { fields: conditions, append: appendCondition, remove: removeCondition } = useFieldArray({ control, name: `conditions` });

  const [conditionMap, setConditionMap] = useState<{ [key: string]: { [key: string]: string } }>();
  const skipNavigateRef = useRef(false);
  const [sendTestRuleIndex, setSendTestRuleIndex] = useState<number | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const sidebarPlaceholderRef = useRef<HTMLDivElement>(null);

  const triggerOptions = [
    { label: 'Valuation', value: "1" },
    { label: 'Registration', value: "2" },
    { label: 'Form Submission', value: "3" },
    { label: 'Portal Qualifier', value: "4" },
    // { label: 'Website Visit', value: "5" },
  ];

  const conditionEntityToFormFields = React.useCallback((condition: INurtureCampaignCondition): ConditionFormFields => {
    var mappedCondition = {
      id: condition.id,
      nurtureCampaignRuleId: condition.nurtureCampaignRuleId,
      type: String(condition.type),
      fieldName: condition.fieldName,
      operator: condition.operator,
      value: condition.value
    };

    if (condition.type === ConditionType.MarketingGroup) {
      // Marketing group type
      return {
        ...mappedCondition,
        marketingGroupIds: condition.value ? condition.value.split(',') : []
      };
    }

    if (condition.type === ConditionType.FormName) {
      // Form name type
      return {
        ...mappedCondition,
        formIds: condition.value ? condition.value.split(',') : []
      };
    }

    if (condition.type === ConditionType.Field) {
      // Default case - also initialize multiValue for multi-select
      return {
        ...mappedCondition,
        multiValue: condition.value ? condition.value.split(',').filter(val => val.trim() !== '') : []
      };
    }

    return mappedCondition;
  }, []);

  const conditionFormFieldsToCreateEntity = (condition: ConditionFormFields): NurtureCampaignConditionCreate => {
    const conditionType = parseInt(condition.type);

    var mappedCondition = {
      type: conditionType,
      fieldName: condition.fieldName,
      operator: condition.operator,
      value: condition.value
    };

    if (conditionType === 1) {
      // Marketing group type
      return {
        ...mappedCondition,
        value: condition.marketingGroupIds ? condition.marketingGroupIds.join(',') : ''
      }
    }

    if (conditionType === 4) {
      // Form name type
      return {
        ...mappedCondition,
        value: condition.formIds ? condition.formIds.join(',') : ''
      }
    }

    return mappedCondition
  };

  const conditionFormFieldsToUpdateEntity = (condition: ConditionFormFields): NurtureCampaignConditionUpdate => ({
    ...conditionFormFieldsToCreateEntity(condition),
    id: condition.id !== "" && condition.id !== undefined ? condition.id : undefined,
    nurtureCampaignRuleId: condition.nurtureCampaignRuleId!
  });

  const ruleEntityToFormFields = React.useCallback((rule: INurtureCampaignRule): RuleFormFields => {
    const delayInHours = rule.delay / 60 / 60;
    const delayDays = delayInHours % 24 === 0;

    return {
      id: rule.id,
      enabled: rule.enabled ? true : false,
      name: rule.name,
      conditions: rule.conditions.map(conditionEntityToFormFields),
      delay: String(delayDays ? delayInHours / 24 : delayInHours), // convert delay from secs to hours
      delaySpan: delayDays ? "days" : "hours",
      action: String(rule.action),
      subject: rule.subject,
      content: rule.content,
      enableButton: rule.enableButton,
      buttonText: rule.buttonText,
      buttonUrl: rule.buttonUrl.includes("{{agency.Domain}}") ? rule.buttonUrl : "CUSTOM_URL",
      nurtureCampaignId: rule.nurtureCampaignId,
      nextRuleId: rule.nextRuleId,
      customUrl: rule.buttonUrl
    };
  }, [conditionEntityToFormFields]);

  const ruleFormFieldsToCreateEntity = (rule: RuleFormFields, nextRuleId?: string): NurtureCampaignRuleCreate => {
    return {
      enabled: rule.enabled,
      name: rule.name,
      conditions: rule.conditions && rule.conditions.length > 0 ? rule.conditions.map(conditionFormFieldsToCreateEntity) : [],
      delay: rule.delay ?
        rule.delaySpan === "days" ?
          parseInt(rule.delay) * 60 * 60 * 24
          : parseInt(rule.delay) * 60 * 60
        : 0, // Convert delay to seconds
      action: parseInt(rule.action),
      subject: rule.subject,
      content: rule.content,
      enableButton: rule.enableButton ? true : false,
      buttonText: rule.buttonText,
      buttonUrl: rule.buttonUrl === "CUSTOM_URL" ? rule.customUrl! : rule.buttonUrl,
      nurtureCampaignId: rule.nurtureCampaignId,
      nextRuleId: nextRuleId
    };
  };

  const ruleFormFieldsToUpdateEntity = (rule: RuleFormFields, nextRuleId?: string): NurtureCampaignRuleUpdate => ({
    ...ruleFormFieldsToCreateEntity(rule, nextRuleId),
    conditions: rule.conditions.map(conditionFormFieldsToUpdateEntity),
    id: rule.id!,
  })

  const orderRulesByNextRuleId = (rules: RuleFormFields[], firstRuleId: string): RuleFormFields[] => {
    //find first rule based off ID
    const firstRule = rules.find((rule) => rule.id === firstRuleId);

    if (!firstRule) return [];

    const orderedRules: RuleFormFields[] = [firstRule];

    let currentRule = firstRule;

    while (currentRule.nextRuleId) {
      const nextRuleId = currentRule.nextRuleId;
      let foundRule = rules.find((rule) => rule.id === nextRuleId);
      if (!foundRule) break;
      currentRule = foundRule;   // If no next rule found, exit the loop
      if (currentRule) orderedRules.push(currentRule);
    }

    return orderedRules;
  };

  const onSubmit = async (data: NurtureCampaignFormFields, shouldEnable = false) => {
    if (nurtureCampaign.data?.data) {
      if (shouldEnable) {
        data.enabled = true;
      }

      // reverse the rules - we do this because we need the nextRuleId which won't exist if we start from the beginning
      const reversedRules = [...data.rules].reverse();

      let nextRuleId: string | undefined = undefined;
      for (const rule of reversedRules) {

        if (shouldEnable) {
          rule.enabled = true;
        }

        rule.conditions = data.conditions.map(condition => ({ ...condition }));

        rule.conditions.forEach(condition => {
          if (conditionMap && conditionMap[rule.id!]) {
            condition.id = conditionMap[rule.id!][condition.id!] || '';
          }
        });

        if (rule.id && rule.id !== "") {
          // if the rule has an ID, we are updating it
          const ruleEntity = ruleFormFieldsToUpdateEntity(rule, nextRuleId);

          await updateRule.mutateAsync({
            ...ruleEntity,
            nextRuleId
          }, {
            // eslint-disable-next-line no-loop-func
            onSuccess: (ruleResponse) => {
              console.log(ruleResponse);
              nextRuleId = ruleResponse.data.id;
            },
            onError: (error) => {
              navigate(`../${nurtureCampaign.data!.data.id}`, { state: { error: true } });
              console.error("Error creating rule:", error);
            }
          });
        } else {
          const ruleEntity = ruleFormFieldsToCreateEntity(rule, nextRuleId);
          await createRule.mutateAsync({
            ...ruleEntity,
            conditions: data.conditions.map(conditionFormFieldsToCreateEntity),
            action: NurtureAction.SendEmail,
            nurtureCampaignId: nurtureCampaign.data!.data.id,
            nextRuleId
          }, {
            // eslint-disable-next-line no-loop-func
            onSuccess: (ruleResponse) => {
              console.log(ruleResponse);
              nextRuleId = ruleResponse.data.id;
            },
            onError: (error) => {
              navigate(`../${nurtureCampaign.data!.data.id}`, { state: { error: true } });
              console.error("Error creating rule:", error);
            }
          });
        }
      }

      // Finally, update the campaign with the firstRuleId
      try {
        await updateCampaign.mutateAsync({
          ...nurtureCampaign.data!.data,
          ...data,
          trigger: parseInt(data.trigger),
          firstRuleId: nextRuleId,
        });
        addNotification({ primaryText: "Nurture campaign saved successfully", variant: "success" });
      } catch (error) {
        addNotification({ primaryText: "Error saving nurture campaign:", variant: "error" });
        console.error("Error updating campaign:", error);
      }

    } else {
      try {
        const response = await createCampaign.mutateAsync({
          ...data,
          trigger: parseInt(data.trigger)
        });

        addNotification({ primaryText: "Nurture campaign created successfully", variant: "success" });

        if (!skipNavigateRef.current) {
          navigate(`../${response.data.id}`);
        }
      } catch (error) {
        addNotification({ primaryText: "Failed to create nurture campaign", variant: "error" });
      }
    }
  };

  // Helper used by children who want to submit without triggering navigation
  const submitWithoutNavigate = async (shouldEnable: boolean) => {
    try {
      skipNavigateRef.current = true;
      await handleSubmit((data) => onSubmit(data, shouldEnable))();
    } finally {
      skipNavigateRef.current = false;
    }
  };

  const extractConditionsFromRules = React.useCallback((rules: INurtureCampaignRule[]): ConditionFormFields[] => {
    if (!rules || rules.length === 0) return [];

    const masterConditions = rules[0].conditions;

    rules.forEach((rule, i) => {
      setConditionMap(prev => ({
        ...prev,
        [rule.id]: masterConditions.reduce<{ [key: string]: string }>((acc, condition, i) => {
          acc[String(condition.id)] = rule.conditions[i]?.id ?? '';
          return acc;
        }, {})
      }));
    });

    return rules[0].conditions.map(conditionEntityToFormFields);
  }, [conditionEntityToFormFields, setConditionMap]);

  useEffect(() => {
    if (!nurtureCampaign.isLoading && nurtureCampaign.data?.data) {
      reset({
        enabled: nurtureCampaign.data.data.enabled,
        name: nurtureCampaign.data.data.name,
        trigger: nurtureCampaign.data.data.trigger.toString(),
        conditions: extractConditionsFromRules(nurtureCampaign.data.data.rules),
        rules: nurtureCampaign.data.data.rules.length > 0 ?
          orderRulesByNextRuleId(nurtureCampaign.data.data.rules.map(ruleEntityToFormFields), nurtureCampaign.data.data.firstRuleId!) : [],
      })
    }
  }, [nurtureCampaign.data, nurtureCampaign.isLoading, reset, extractConditionsFromRules, ruleEntityToFormFields]);

  useEffect(() => {
    const handleScroll = () => {
      if (sidebarPlaceholderRef.current && sidebarRef.current) {
        const rect = sidebarPlaceholderRef.current.getBoundingClientRect();
        const shouldStick = rect.top <= 16; // 16px = top-4 in tailwind

        setIsSticky(shouldStick);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [nurtureCampaign.data?.data]);

  const pages = [
    { to: "..", label: "Nurturing Campaigns", current: false },
    {
      to: campaignId && nurtureCampaign.data ? `${campaignId}` : "",
      label: campaignId ? `${nurtureCampaign.data?.data.name}` : "New Campaign",
      current: true,
    },
  ];

  if (nurtureCampaign.isLoading || !agencyReady) {
    return <div className="flex py-10 justify-center"><LoadingWheel width={"w-10"} height={"h-10"} /></div>
  } else {
    return (
      <>
        <form ref={formRef} onSubmit={handleSubmit((data) => onSubmit(data, false))} className="flex flex-col">
          <div className='px-4 sm:px-6 w-full'>
            <Breadcrumbs pages={pages} />
          </div>
          <div className="flex flex-row mt-4 items-start">
            <div className="px-4 sm:px-6 w-2/3 lg:px-8 space-y-4 mb-8">

              <div className="space-y-4">
                {state?.error &&
                  <Alert
                    type='error'
                    title="An Error Occurred"
                    message="It looks like we ran into an issue while saving the campaign, below is what was saved before the error occurred, please try again, if the issue persists. Please contact support"
                  />
                }
                <BaseCard
                  title="Step 1 - Campaign Details"
                  subtitle="Define the details of your nurturing campaign."
                >
                  {!nurtureCampaign.data?.data &&
                    <div className="pb-4 text-sm text-gray-700">
                      <p>To get started creating a nurturing campaign, please give it a friendly name and define what action will trigger the start of the campaign</p>
                    </div>
                  }
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <StyledTextInput
                      register={register}
                      registerOptions={{ required: "A Name is required" }}
                      registerName="name"
                      label="Campaign Name"
                      classes="mt-1"
                      colsClass="lg:col-span-2"
                      description="A friendly name for this campaign"
                      error={errors.name?.message}
                    />
                    <div className="lg:col-span-2">
                      <StyledSelectInput
                        label="Trigger"
                        register={register}
                        registerName="trigger"
                        options={triggerOptions}
                        setValue={setValue}
                        watch={watch}
                        description="The event that triggers this campaign"
                      />
                    </div>
                    <div className="flex items-center mb-3 justify-center">
                      <StyledSwitchInput
                        register={register}
                        registerName="enabled"
                        label="Running"
                        setValue={setValue}
                        watch={watch}
                      />
                    </div>
                  </div>
                </BaseCard>

                {nurtureCampaign.data?.data &&
                  <BaseCard title="Step 2 - Filters" subtitle="Filters allow you to narrow down the audience for your campaign based on specific criteria.">
                    <div className="sm:col-span-2 lg:col-span-5">

                      <div className="mt-2 space-y-2">
                        {conditions.map((condition, conditionIndex) => (
                          <React.Fragment key={condition.id || conditionIndex}>
                            <ConditionRow
                              conditionIndex={conditionIndex}
                              register={register}
                              onDelete={removeCondition}
                              setValue={setValue}
                              watch={watch}
                            />
                            {conditionIndex < conditions.length - 1 && (
                              <p className="text-gray-500 text-sm text-center">AND</p>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="mt-4">
                        {/* @ts-ignore */}
                        <ButtonInput onClick={() => appendCondition({ type: "0", fieldName: "", operator: "=", value: "", multiValue: [], formIds: [] })}>Add Condition</ButtonInput>
                      </div>
                    </div>
                  </BaseCard>
                }
              </div>

              {nurtureCampaign.data?.data ?
                <>
                  <div className="mt-6">
                    {rules.length > 0 &&
                      <h1 className="text-2xl font-semibold">Emails</h1>
                    }
                    <div className="my-6 space-y-16">
                      {rules.map((rule, index) => (
                        <RuleCard
                          key={rule.id || index}
                          register={register}
                          setValue={setValue}
                          watch={watch}
                          getValues={getValues}
                          onDelete={removeRule}
                          ruleIndex={index}
                          move={moveRule}
                          isFirst={index === 0}
                          isLast={index === rules.length - 1}
                          errors={errors}
                          onSubmitCampaign={() => submitWithoutNavigate(false)}
                          onOpenSendTest={(idx) => setSendTestRuleIndex(idx)}
                          {...rule}
                        />
                      ))}
                      <SendTestModal
                        open={sendTestRuleIndex !== null}
                        setOpen={(open: boolean) => { if (!open) setSendTestRuleIndex(null); }}
                        campaignId={sendTestRuleIndex !== null ? getValues(`rules.${sendTestRuleIndex}.nurtureCampaignId`) || "" : ""}
                        ruleId={sendTestRuleIndex !== null ? getValues(`rules.${sendTestRuleIndex}.id`) || "" : ""}
                      />
                    </div>
                  </div>
                  <div className="w-full">
                    {/* @ts-ignore  */}
                    <ButtonInput
                      label={rules.length > 0 ? 'Add Email' : 'Step 3 - Add Email'}
                      classes="!w-full"
                      onClick={() => appendRule(ruleTemplate as RuleFormFields)}
                    />
                  </div>
                </>
                :
                <div className="w-full">
                  {/* @ts-ignore  */}
                  <ButtonInput
                    label="Create Campaign"
                    classes="!w-full"
                    isSubmit={true}
                    onClick={() => { }}
                  />
                </div>
              }
            </div>
            <aside className="w-1/3 px-4 relative">
              {nurtureCampaign.data?.data &&
                <>
                  <div ref={sidebarPlaceholderRef} style={{ height: isSticky ? `${sidebarRef.current?.offsetHeight || 0}px` : '0' }} />
                  <div
                    ref={sidebarRef}
                    className={`bg-white border rounded-lg p-4 space-y-4 shadow z-50 ${isSticky ? 'fixed top-20 w-[calc(33.333%-7rem)]' : ''}`}
                  >
                    {/* @ts-ignore */}
                    <ButtonInput
                      classes="!w-full"
                      isSubmit={true}
                      label="Save Campaign"
                    />
                    {/* @ts-ignore */}
                    <ButtonInput
                      classes="!w-full"
                      isSubmit={false}
                      onClick={() => submitWithoutNavigate(true)}
                      label="Save and Start Campaign"
                    />
                    <div className="mt-4">
                      {/* @ts-ignore */}
                      <ButtonInput
                        classes="!w-full"
                        isSubmit={false}
                        label="Add Email"
                        onClick={() => appendRule({
                          enabled: true,
                          delay: '1',
                          delaySpan: "days",
                          buttonText: "Click Here",
                          buttonUrl: "{{websiteUrl}}/area-guide"
                        } as RuleFormFields)
                        }
                      />
                    </div>
                  </div>
                </>
              }
            </aside>
          </div>
        </form>
      </>
    )
  }
}

const NurturingCampaignBuilder = withAgency(_NurturingCampaignBuilder);

export default NurturingCampaignBuilder;